// CotacaoComparativo — portagem fiel do ControleFC (frontend/src/pages/Cotacoes/CotacaoComparativo.jsx).
// Painel com produtos x fornecedores: menor Unit. c/ Imp destacado em amarelo.
// Total c Imp do fornecedor define o fator de imposto/frete (rateio sobre soma de pu*qtd).
import { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ImageOff } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { FotoProduto } from '../../components/FotoProduto';
import { formatMoedaSemSimbolo, getNomeFornecedor } from '@fiodecobre/shared';

interface Item {
  id: number;
  codigo_interno: string | null;
  /** O codigo da prateleira nova ([DA-610]) — mesma coluna, selo proprio. */
  np_codigo?: string | null;
  produto_descricao: string | null;
  foto_url: string | null;
  quantidade: string;
}

interface Fornecedor {
  fornecedor_id: number;
  total_c_imp: string | null;
  razao_social: string | null;
  nome_fantasia: string | null;
}

interface Resposta {
  id: number;
  cotacao_item_id: number;
  fornecedor_id: number;
  cod_produto_fornecedor: string | null;
  marca: string | null;
  descricao_complementar: string | null;
  embalagem: string | null;
  quantidade_embalagem: string | null;
  quantidade_cotada: string | null;
  preco_principal: string | null;
  preco_unitario: string | null;
  ipi_percentual: string | null;
}

interface Comparativo {
  numero: string;
  status: string;
  itens: Item[];
  fornecedores: Fornecedor[];
  respostas: Resposta[];
}

export function CotacaoComparativo() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const [dados, setDados] = useState<Comparativo | null>(null);
  const [loading, setLoading] = useState(true);
  const [fotoZoom, setFotoZoom] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api
      .get<Comparativo>(`/api/cotacoes/${id}/comparativo`)
      .then((r) => setDados(r))
      .catch((e: unknown) => toast.erro(e instanceof ApiError ? e.message : 'Erro ao carregar comparativo'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>Carregando...</div>;
  if (!dados) return null;

  const getNomeForn = (fornId: number): string => {
    const f = dados.fornecedores.find((x) => x.fornecedor_id === fornId);
    return getNomeFornecedor(f);
  };

  // Sem rateio do "Total c/Imp" desde 29/10/2026 ([DA-864]): o campo ficava com o 100 do default e
  // encolhia os precos (total 100 / soma real). Vale so o IPI gravado na linha.
  const getUci = (r: Resposta): number => {
    const pu = parseFloat(r.preco_unitario || '0') || 0;
    if (r.ipi_percentual != null) {
      return pu * (1 + parseFloat(r.ipi_percentual) / 100);
    }
    return pu;
  };

  // Agrupa respostas por item, ordena por nome do fornecedor
  const respostasPorItem: Record<number, Resposta[]> = {};
  for (const r of dados.respostas) {
    const pp = parseFloat(r.preco_principal || '0') || 0;
    if (pp <= 0) continue;
    if (!respostasPorItem[r.cotacao_item_id]) respostasPorItem[r.cotacao_item_id] = [];
    respostasPorItem[r.cotacao_item_id]!.push(r);
  }
  for (const itemId of Object.keys(respostasPorItem)) {
    respostasPorItem[Number(itemId)]!.sort((a, b) =>
      getNomeForn(a.fornecedor_id).localeCompare(getNomeForn(b.fornecedor_id)),
    );
  }

  return (
    <div className="modulo-pagina" style={{ padding: 16 }}>
      {/* Header */}
      <div className="pagina-header">
        <div>
          <h2 className="pagina-titulo" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={() => navigate(`/cotacoes/${id}/respostas`)}
              className="btn-icon"
              title="Voltar"
              aria-label="Voltar"
              style={{ marginRight: 4 }}
            >
              <ArrowLeft size={20} />
            </button>
            FORNECEDORES — COMPARATIVO DE PRECOS
          </h2>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>{dados.numero}</p>
        </div>
        <div className="pagina-acoes">
          <button
            type="button"
            onClick={() => navigate(`/cotacoes/${id}/respostas`)}
            className="btn-secondary"
            style={{ fontSize: 13 }}
          >
            Editar Precos
          </button>
        </div>
      </div>

      <div
        style={{
          background: 'var(--panel-bg)',
          border: '1px solid var(--panel-border, #e5e7eb)',
          borderRadius: 6,
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--border)', borderBottom: '1px solid #9ca3af', color: 'var(--page-fg)' }}>
              <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, width: 140 }}>FORNECEDOR</th>
              <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, width: 110 }}>Cod. Fornec.</th>
              <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600 }}>Desc. Fornecedor</th>
              <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, width: 130 }}>Marca</th>
              <th style={{ textAlign: 'center', padding: '6px 12px', fontWeight: 600, width: 80 }}>Embal.</th>
              <th style={{ textAlign: 'center', padding: '6px 12px', fontWeight: 600, width: 80 }}>Qtd na Emb</th>
              <th style={{ textAlign: 'center', padding: '6px 12px', fontWeight: 600, width: 80 }}>Qtd de Emb</th>
              <th style={{ textAlign: 'right', padding: '6px 12px', fontWeight: 600, width: 90 }}>Preco (R$)</th>
              <th style={{ textAlign: 'right', padding: '6px 12px', fontWeight: 600, width: 80 }}>Unit. (R$)</th>
              <th
                style={{ textAlign: 'right', padding: '6px 12px', fontWeight: 600, width: 95 }}
                title="Unitario com rateio de imposto/frete pelo Total c Imp do fornecedor"
              >
                Unit. c/ Imp
              </th>
            </tr>
          </thead>
          <tbody>
            {dados.itens.map((item) => {
              const respostas = respostasPorItem[item.id] || [];
              const ucis = respostas.map((r) => getUci(r)).filter((v) => v > 0);
              const menorUci = ucis.length > 0 ? Math.min(...ucis) : 0;

              return (
                <Fragment key={item.id}>
                  {/* Cabecalho do produto */}
                  <tr style={{ background: 'var(--grid-row-alt-bg)', borderTop: '2px solid #d1d5db' }}>
                    <td colSpan={10} style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {item.foto_url ? (
                          <FotoProduto
                            url={item.foto_url}
                            tam={40}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setFotoZoom(item.foto_url)}
                          />
                        ) : (
                          <div
                            style={{
                              width: 40,
                              height: 40,
                              background: 'var(--border)',
                              borderRadius: 4,
                              border: '1px solid var(--border)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <ImageOff size={14} color="#9ca3af" />
                          </div>
                        )}
                        <div>
                          <span style={{ fontWeight: 700, color: 'var(--primary)', marginRight: 12 }}>
                            {item.codigo_interno || (item.np_codigo && (
                              <span style={{ background: '#c4b5fd', color: '#000', borderRadius: 3, padding: '1px 5px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}
                                    title="Item da prateleira Novos Produtos — ainda nao virou produto do cadastro">
                                {item.np_codigo}
                              </span>
                            ))}
                          </span>
                          <span style={{ fontWeight: 700, color: 'var(--page-fg)' }}>{item.produto_descricao}</span>
                        </div>
                        {respostas.length === 0 && (
                          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 16 }}>
                            Nenhum fornecedor informou preco
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Linhas dos fornecedores */}
                  {respostas.map((r, idx) => {
                    const uci = getUci(r);
                    const isMenor = uci > 0 && menorUci > 0 && uci === menorUci;
                    return (
                      <tr
                        key={`${item.id}-${r.id}`}
                        style={{
                          borderBottom: '1px solid var(--border)',
                          background: idx % 2 === 1 ? 'var(--grid-row-alt-bg)' : 'var(--panel-bg)',
                        }}
                      >
                        <td
                          style={{
                            padding: '6px 12px',
                            fontWeight: 600,
                            color: isMenor ? '#15803d' : 'var(--page-fg)',
                            maxWidth: 140,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={getNomeForn(r.fornecedor_id)}
                        >
                          {getNomeForn(r.fornecedor_id)}
                        </td>
                        <td style={{ padding: '6px 12px', color: 'var(--page-fg)' }}>{r.cod_produto_fornecedor || '—'}</td>
                        <td style={{ padding: '6px 12px', color: 'var(--page-fg)' }}>{r.descricao_complementar || '—'}</td>
                        <td style={{ padding: '6px 12px', color: 'var(--page-fg)' }}>{r.marca || '—'}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'center', color: 'var(--page-fg)' }}>{r.embalagem || 'UN'}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'center', color: 'var(--page-fg)' }}>
                          {Number(r.quantidade_embalagem || 0).toLocaleString('pt-BR')}
                        </td>
                        <td style={{ padding: '6px 12px', textAlign: 'center', color: 'var(--page-fg)' }}>
                          {Number(r.quantidade_cotada || 1).toLocaleString('pt-BR')}
                        </td>
                        <td style={{ padding: '6px 12px', textAlign: 'right', color: 'var(--page-fg)' }}>
                          {formatMoedaSemSimbolo(r.preco_principal)}
                        </td>
                        <td style={{ padding: '6px 12px', textAlign: 'right', color: 'var(--text-muted)' }}>
                          {formatMoedaSemSimbolo(r.preco_unitario)}
                        </td>
                        <td
                          style={{
                            padding: '6px 12px',
                            textAlign: 'right',
                            fontWeight: 700,
                            background: isMenor ? 'var(--positive-bg)' : 'transparent',
                            color: isMenor ? 'var(--positive-fg)' : 'var(--page-fg)',
                          }}
                        >
                          {uci > 0 ? formatMoedaSemSimbolo(uci) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal de zoom da foto */}
      {fotoZoom && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
        >
          <button
            type="button"
            className="btn-fechar-modal btn-fechar-modal--abs"
            onClick={() => setFotoZoom(null)}
            aria-label="Fechar"
            style={{ color: '#fff' }}
          >
            ×
          </button>
          <img
            src={fotoZoom}
            alt="Zoom"
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 8,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          />
        </div>
      )}
    </div>
  );
}
